import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "@redq/reuse-modal";
import { connect } from "react-redux";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import CloseIcon from "@material-ui/icons/Close";
import StarIcon from "@material-ui/icons/Star";
import DeleteSweepOutlinedIcon from "@material-ui/icons/DeleteSweepOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import UploadService from "../../services/FileUploadService";
import { getSuppliers, setError, setLoading } from "../../store/actions/dashboardAction";

const MAX_FILES = 100;
// Mirrors form.maxFileSize on POST /api/admin/product/image/upload.
const MAX_TOTAL_BYTES = 15 * 1024 * 1024;

// Server-side reasons, in words an admin can act on.
const REASON_LABEL = {
  NO_MATCHING_SKU: "No product has this SKU",
  INVALID_SEQUENCE: "Image number must start at 1",
  IMAGE_CONVERSION_FAILED: "File could not be read as an image",
  UPLOAD_FAILED: "Upload failed — try this file again",
};

// Same split the server uses: greedy on the SKU half, so "COKE-330-2" reads as
// SKU "COKE-330", image 2.
const SECONDARY_SUFFIX = /^(.+)-(\d+)$/;

function describeFileName(fileName) {
  const dot = fileName.lastIndexOf(".");
  const base = (dot > 0 ? fileName.slice(0, dot) : fileName).trim();
  const match = SECONDARY_SUFFIX.exec(base);

  if (match && Number(match[2]) >= 1) {
    return { sku: match[1], sequence: Number(match[2]) };
  }
  return { sku: base, sequence: null };
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const useStyles = makeStyles((theme) => ({
  page: {
    backgroundColor: "#f4f6f8",
    minHeight: "100vh",
    padding: theme.spacing(5, 0),
    [theme.breakpoints.down("sm")]: { paddingTop: theme.spacing(9) },
  },
  header: { marginBottom: theme.spacing(3) },
  convention: {
    padding: theme.spacing(2, 2.5),
    marginBottom: theme.spacing(3),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  code: {
    fontFamily: "Menlo, Consolas, monospace",
    backgroundColor: "rgba(0,0,0,0.06)",
    borderRadius: 4,
    padding: "2px 6px",
    fontSize: 13,
  },
  dropzone: {
    border: "2px dashed rgba(0,0,0,0.18)",
    borderRadius: 12,
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(6, 3),
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color .15s ease, background-color .15s ease",
    outline: "none",
    "&:hover": { borderColor: theme.palette.primary.main },
  },
  dropzoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(0,0,0,0.02)",
  },
  dropIcon: { fontSize: 44, color: theme.palette.primary.main, marginBottom: theme.spacing(1) },
  toolbar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(2, 2.5),
    marginTop: theme.spacing(3),
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  spacer: { flexGrow: 1 },
  group: { padding: theme.spacing(2, 2.5, 2.5), marginTop: theme.spacing(2) },
  groupHead: { display: "flex", alignItems: "center", gap: theme.spacing(1), marginBottom: theme.spacing(1.5) },
  sku: { fontWeight: 600, letterSpacing: 0.2 },
  card: {
    position: "relative",
    borderRadius: 10,
    overflow: "hidden",
    border: "1px solid rgba(0,0,0,0.08)",
    backgroundColor: theme.palette.common.white,
    "&:hover $remove": { opacity: 1 },
  },
  thumbWrap: {
    position: "relative",
    paddingTop: "100%",
    backgroundColor: "#eceff1",
  },
  thumb: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  badge: { position: "absolute", top: 8, left: 8, zIndex: 1 },
  remove: {
    position: "absolute",
    top: 4,
    right: 4,
    zIndex: 1,
    opacity: 0,
    transition: "opacity .15s ease",
    backgroundColor: "rgba(255,255,255,0.92)",
    "&:hover": { backgroundColor: theme.palette.common.white },
  },
  meta: { padding: theme.spacing(1, 1.25, 1.25) },
  fileName: {
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: 13,
  },
  results: { padding: theme.spacing(2, 2.5), marginTop: theme.spacing(3) },
  failureRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
  },
}));

const BulkImageUplodes = () => {
  const classes = useStyles();
  const inputRef = useRef(null);

  const admin = typeof window !== "undefined" ? localStorage.getItem("admin") : null;
  if (!admin) {
    window.location.href = "/";
  }

  // Each item keeps the File plus the object URL its preview renders from, so
  // removing one can revoke exactly that URL and leak nothing.
  const [items, setItems] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [response, setResponse] = useState(null);
  const [notice, setNotice] = useState(null);

  // Revoking on unmount needs the CURRENT items, not the ones captured when the
  // effect first ran.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => () => itemsRef.current.forEach((item) => URL.revokeObjectURL(item.url)), []);

  const totalBytes = useMemo(() => items.reduce((sum, item) => sum + item.file.size, 0), [items]);
  const mainCount = useMemo(() => items.filter((item) => item.sequence === null).length, [items]);

  // One panel per SKU, main image first, then gallery images in their numbered
  // order — so a missing #3 is obvious before anything is uploaded.
  const groups = useMemo(() => {
    const bySku = new Map();
    items.forEach((item) => {
      if (!bySku.has(item.sku)) bySku.set(item.sku, []);
      bySku.get(item.sku).push(item);
    });
    return [...bySku.entries()]
      .map(([sku, groupItems]) => ({
        sku,
        items: [...groupItems].sort((a, b) => (a.sequence ?? -1) - (b.sequence ?? -1)),
      }))
      .sort((a, b) => a.sku.localeCompare(b.sku));
  }, [items]);

  const addFiles = useCallback((fileList) => {
    const incoming = [...fileList].filter((file) => file.type.startsWith("image/"));
    const rejected = fileList.length - incoming.length;

    setItems((previous) => {
      const next = [...previous];
      incoming.forEach((file) => {
        // Same file name = same target slot, so a re-pick replaces rather than
        // queues a duplicate the server would process twice.
        const existing = next.findIndex((item) => item.name === file.name);
        if (existing !== -1) {
          URL.revokeObjectURL(next[existing].url);
          next.splice(existing, 1);
        }
        next.push({
          id: `${file.name}-${file.lastModified}-${file.size}`,
          name: file.name,
          file,
          url: URL.createObjectURL(file),
          ...describeFileName(file.name),
        });
      });
      return next.slice(0, MAX_FILES);
    });

    const messages = [];
    if (rejected > 0) messages.push(`${rejected} file(s) skipped — not an image.`);
    if (fileList.length > MAX_FILES) messages.push(`Only the first ${MAX_FILES} files were kept.`);
    setNotice(messages.length ? messages.join(" ") : null);
    setResponse(null);
  }, []);

  const removeItem = useCallback((id) => {
    setItems((previous) => {
      const target = previous.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return previous.filter((item) => item.id !== id);
    });
  }, []);

  const clearAll = useCallback(() => {
    setItems((previous) => {
      previous.forEach((item) => URL.revokeObjectURL(item.url));
      return [];
    });
    setResponse(null);
    setNotice(null);
  }, []);

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length) {
      addFiles(event.dataTransfer.files);
    }
  };

  const handleSubmit = async () => {
    if (!items.length || uploading) return;

    const formData = new FormData();
    items.forEach((item) => formData.append("file", item.file));

    setUploading(true);
    setProgress(0);
    setResponse(null);

    try {
      const { data } = await UploadService.bulkUploadImage(formData, (event) =>
        setProgress(Math.round((100 * event.loaded) / event.total))
      );
      const results = Array.isArray(data.results) ? data.results : [];
      setResponse({ ...data, results });

      // Clear what landed, keep what did not so the admin can fix those names
      // and retry just them.
      const uploaded = new Set(results.filter((r) => r.status === "uploaded").map((r) => r.file));
      if (uploaded.size) {
        setItems((previous) => {
          previous.filter((item) => uploaded.has(item.name)).forEach((item) => URL.revokeObjectURL(item.url));
          return previous.filter((item) => !uploaded.has(item.name));
        });
      }
    } catch (error) {
      const data = (error && error.response && error.response.data) || {};
      setResponse({ success: false, message: data.message || "Upload failed", results: [] });
    } finally {
      setUploading(false);
    }
  };

  const failures = (response && response.results ? response.results : []).filter(
    (result) => result.status !== "uploaded"
  );
  const overSize = totalBytes > MAX_TOTAL_BYTES;

  return (
    <Modal>
      <div className={classes.page}>
        <Container maxWidth="lg">
          <div className={classes.header}>
            <Typography variant="h5" component="h1" gutterBottom>
              Bulk Image Upload
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Images are matched to products by file name, converted to WebP, and published.
            </Typography>
          </div>

          <Paper elevation={0} className={classes.convention}>
            <Typography variant="subtitle2" gutterBottom>
              File naming
            </Typography>
            <Typography variant="body2" color="textSecondary" component="div">
              <Box mb={0.5}>
                <span className={classes.code}>[sku].jpg</span> — main product image
              </Box>
              <Box mb={0.5}>
                <span className={classes.code}>[sku]-1.jpg</span>,{" "}
                <span className={classes.code}>[sku]-2.jpg</span> — secondary images, shown in that order
              </Box>
              <Box>If a SKU itself ends in a number, that file is treated as that product&apos;s main image.</Box>
            </Typography>
          </Paper>

          <div
            className={`${classes.dropzone} ${dragging ? classes.dropzoneActive : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current && inputRef.current.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") inputRef.current.click();
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            <CloudUploadOutlinedIcon className={classes.dropIcon} />
            <Typography variant="subtitle1">Drag images here, or click to browse</Typography>
            <Typography variant="body2" color="textSecondary">
              Up to {MAX_FILES} images per upload · 15 MB total
            </Typography>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(event) => {
                addFiles(event.target.files);
                event.target.value = "";
              }}
            />
          </div>

          {notice && (
            <Box mt={2}>
              <Alert severity="info" onClose={() => setNotice(null)}>
                {notice}
              </Alert>
            </Box>
          )}

          {items.length > 0 && (
            <>
              <Paper className={classes.toolbar}>
                <Typography variant="subtitle1">
                  {items.length} image{items.length === 1 ? "" : "s"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  · {mainCount} main · {items.length - mainCount} secondary · {formatBytes(totalBytes)}
                </Typography>
                <span className={classes.spacer} />
                <Button
                  size="small"
                  startIcon={<DeleteSweepOutlinedIcon />}
                  onClick={clearAll}
                  disabled={uploading}
                >
                  Clear all
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  onClick={handleSubmit}
                  disabled={uploading || overSize}
                >
                  {uploading ? `Uploading ${progress}%` : `Upload ${items.length} image${items.length === 1 ? "" : "s"}`}
                </Button>
              </Paper>

              {uploading && <LinearProgress variant="determinate" value={progress} />}

              {overSize && (
                <Box mt={2}>
                  <Alert severity="warning">
                    This batch is {formatBytes(totalBytes)}. The server accepts 15 MB per upload — remove some images
                    and send them in a second batch.
                  </Alert>
                </Box>
              )}

              {groups.map((group) => (
                <Paper key={group.sku} className={classes.group} elevation={0}>
                  <div className={classes.groupHead}>
                    <Typography variant="subtitle2" className={classes.sku}>
                      {group.sku}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {group.items.length} image{group.items.length === 1 ? "" : "s"}
                    </Typography>
                  </div>
                  <Grid container spacing={2}>
                    {group.items.map((item) => (
                      <Grid item key={item.id} xs={6} sm={4} md={3} lg={2}>
                        <div className={classes.card}>
                          <div className={classes.thumbWrap}>
                            <img src={item.url} alt={item.name} className={classes.thumb} />
                            <div className={classes.badge}>
                              {item.sequence === null ? (
                                <Chip
                                  size="small"
                                  color="primary"
                                  icon={<StarIcon style={{ fontSize: 14 }} />}
                                  label="Main"
                                />
                              ) : (
                                <Tooltip title={`Gallery position ${item.sequence}`}>
                                  <Chip size="small" label={`#${item.sequence}`} />
                                </Tooltip>
                              )}
                            </div>
                            {/* span wrapper: Tooltip cannot hold a disabled button */}
                            <span className={classes.remove}>
                              <Tooltip title="Remove">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => removeItem(item.id)}
                                    disabled={uploading}
                                    aria-label={`Remove ${item.name}`}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </span>
                          </div>
                          <div className={classes.meta}>
                            <Tooltip title={item.name}>
                              <Typography variant="body2" className={classes.fileName}>
                                {item.name}
                              </Typography>
                            </Tooltip>
                            <Typography variant="caption" color="textSecondary">
                              {formatBytes(item.file.size)}
                            </Typography>
                          </div>
                        </div>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              ))}
            </>
          )}

          {response && (
            <Paper className={classes.results} elevation={0}>
              <Alert severity={failures.length ? "warning" : "success"}>
                <AlertTitle>{failures.length ? "Uploaded with problems" : "Uploaded"}</AlertTitle>
                {response.message}
              </Alert>

              {failures.length > 0 && (
                <Box mt={2}>
                  <Divider />
                  <Box mt={1.5}>
                    {failures.map((failure, index) => (
                      <div className={classes.failureRow} key={`${failure.file}-${index}`}>
                        <ErrorOutlineIcon fontSize="small" color="error" />
                        <Typography variant="body2" style={{ minWidth: 220 }}>
                          {failure.file}
                        </Typography>
                        <Chip size="small" variant="outlined" label={REASON_LABEL[failure.reason] || failure.reason} />
                      </div>
                    ))}
                  </Box>
                  <Box mt={1.5}>
                    <Typography variant="caption" color="textSecondary">
                      These files are still queued above — rename them and upload again.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Container>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  suppliers: state.dashboardReducer.suppliers,
  success: state.dashboardReducer.success,
  loading: state.dashboardReducer.loading,
  error_message: state.dashboardReducer.error_message,
});

const mapDispatchToProps = (dispatch) => ({
  getSuppliers: (data) => getSuppliers(data, dispatch),
  setError: (data) => setError(data, dispatch),
  setLoading: (data) => setLoading(data, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkImageUplodes);
